import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Loader2, FileVideo, CheckCircle2, Circle, Pencil, X } from 'lucide-react';

function StageChip({ label, done, illustrative }) {
  return (
    <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${done ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-white/10 text-[#555E6E] bg-white/[0.02]'}`} data-testid={`queue-stage-${label.toLowerCase()}`}>
      {done ? <CheckCircle2 className="h-2.5 w-2.5" /> : <Circle className="h-2.5 w-2.5" />}
      {label}{illustrative && !done ? ' (demo)' : ''}
    </span>
  );
}

/** Shared Create/Existing project form: name field + the 3x3 Production
 * Tools grid. Used by Workspace.jsx (mode="create", default full form) and
 * the Production Hub (ProjectOverview.jsx, mode="existing",
 * showNameField=false — the Hub renders the name field itself above its
 * two-column layout and only needs the tools grid here). Production Queue
 * is rendered separately via <ProductionQueueDemo> below. */
export default function ProjectFormDemo({
  mode, name, onNameChange, onSubmit, submitting, submitDisabled, showNameField = true,
  actions, activeToolKey, appliedTemplate, onCustomizeTemplate, onChangeTemplate, onRemoveTemplate,
}) {
  const isCreate = mode === 'create';

  return (
    <div className="space-y-5" data-testid="project-form-demo">
      {showNameField && (
        <>
          <div>
            <Label className="text-[#8A93A6]">Project Name</Label>
            <Input
              autoFocus={isCreate}
              value={name}
              onChange={e => onNameChange(e.target.value)}
              placeholder="e.g. Album Rollout"
              className="bg-white/5 border-white/10 text-white mt-1"
              data-testid="project-form-name-input"
            />
          </div>

          <Button onClick={onSubmit} disabled={submitDisabled || submitting} className="w-full sm:w-auto bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" data-testid="project-form-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isCreate ? 'Create Project' : 'Save Changes'}
          </Button>
        </>
      )}

      <div>
        <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Production Tools</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2" data-testid="production-tools-grid">
          {actions.map(a => {
            const isActive = a.key === activeToolKey;
            return (
              <button
                key={a.key}
                type="button"
                onClick={a.disabled ? undefined : a.onClick}
                disabled={a.disabled}
                data-active={isActive}
                className={`flex flex-col items-center justify-center gap-1.5 h-24 rounded-lg border p-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A7BFF]/60 disabled:opacity-35 disabled:cursor-not-allowed ${
                  isActive
                    ? 'border-[#3A7BFF] bg-[#3A7BFF]/15'
                    : 'border-white/10 enabled:hover:bg-white/5 enabled:hover:border-blue-400/40'
                }`}
                data-testid={`production-tool-${a.key}`}
              >
                <a.icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-[#5B9BFF]' : 'text-[#3A7BFF]'}`} />
                <span className="text-[11px] text-white leading-tight">{a.label}</span>
              </button>
            );
          })}
        </div>
        {isCreate && (
          <p className="text-[11px] text-[#555E6E] mt-2">
            {submitDisabled ? 'Enter a project name to activate these tools.' : 'Pick a tool — the project is created automatically, then it opens.'}
          </p>
        )}
      </div>

      {appliedTemplate && !isCreate && (
        <div className="rounded-lg border border-purple-500/25 bg-purple-500/[0.06] p-3 space-y-2" data-testid="applied-template-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white font-medium">Production Blueprint: {appliedTemplate.name}</p>
            <button onClick={onRemoveTemplate} className="text-[#555E6E] hover:text-red-400" data-testid="remove-applied-template-btn"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="flex flex-wrap gap-1">
            {appliedTemplate.segments.map(s => <Badge key={s.id} className="bg-white/5 text-[#C0C7D4] border-white/10 text-[10px]">{s.name}</Badge>)}
          </div>
          <p className="text-[11px] text-[#555E6E]">Applied locally to this project (demo) — editing it never changes the original template.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={onCustomizeTemplate} data-testid="customize-applied-template-btn"><Pencil className="h-3 w-3 mr-1" />Customize</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={onChangeTemplate} data-testid="change-applied-template-btn">Change Template</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Production Queue — existing projects only. Rendered standalone by the
 * Production Hub after Associated Media. */
export function ProductionQueueDemo({ parentAssets = [], thumbMap = {} }) {
  return (
    <div data-testid="production-queue-section">
      <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Production Queue</Label>
      {parentAssets.length === 0 ? (
        <p className="text-xs text-[#555E6E] mt-2">No media uploaded yet — the production queue fills in once you add assets.</p>
      ) : (
        <div className="space-y-2 mt-2" data-testid="production-queue-list">
          {parentAssets.map(a => {
            const hasThumb = !!thumbMap[a.id];
            const edited = a.status && a.status !== 'pending';
            const clipped = (a.child_count || 0) > 0;
            return (
              <div key={a.id} className="flex items-center justify-between gap-2 flex-wrap bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2" data-testid={`queue-row-${a.id}`}>
                <span className="flex items-center gap-1.5 text-xs text-white min-w-0 max-w-[220px] truncate">
                  <FileVideo className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                  {a.title || a.input_file_url?.split('/').pop() || 'Untitled asset'}
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  <StageChip label="Uploaded" done />
                  <StageChip label="Editing" done={edited} />
                  <StageChip label="Clipping" done={clipped} />
                  <StageChip label="Thumbnail" done={hasThumb} />
                  <StageChip label="Optimize" done={false} illustrative />
                  <StageChip label="Scheduled" done={false} illustrative />
                  <StageChip label="Published" done={false} illustrative />
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-[#555E6E]">Uploaded / Editing / Clipping / Thumbnail reflect real project data. Optimize / Scheduled / Published are illustrative until the Scheduler is connected.</p>
        </div>
      )}
    </div>
  );
}
